// src/components/MarketInsights/components/UnitDetailDrawer.tsx

import React, { useMemo } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Button,
  Divider,
  Chip,
  Avatar,
  Grid,
  Card,
  CardContent,
  Link,
  Skeleton,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Close as CloseIcon,
  Apartment as ApartmentIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Language as WebIcon,
  Pets as PetsIcon,
  CheckCircle as CheckIcon,
  Event as EventIcon,
  SquareFoot as SqftIcon,
  KingBed as BedIcon,
  Bathtub as BathIcon,
  AttachMoney as MoneyIcon,
  OpenInNew as OpenIcon,
} from "@mui/icons-material";
import { UnitDetail } from "../types";
import { TOUCH_TARGET } from "../../../theme/theme";
import { resolveExternalPropertyLink } from "../utils/externalPropertyLink";
import {
  formatCurrency,
  formatBedBath,
  formatSqft,
  formatPricePerSqft,
  formatDate,
  formatAvailability,
} from "../utils/formatters";

interface UnitDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  unit: UnitDetail | null;
  isLoading: boolean;
  onPropertyClick?: (propertyId: string) => void;
}

const UnitDetailDrawer: React.FC<UnitDetailDrawerProps> = ({
  open,
  onClose,
  unit,
  isLoading,
  onPropertyClick,
}) => {
  const theme = useTheme();

  const availability = unit
    ? formatAvailability(unit.status, unit.available_date)
    : null;
  const externalApplication = useMemo(
    () => resolveExternalPropertyLink(unit?.apply_url),
    [unit?.apply_url]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 450 } },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Unit Details
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close unit details"
            sx={{ minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {isLoading ? (
            <Box>
              <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
              <Skeleton width="60%" height={32} />
              <Skeleton width="40%" height={24} sx={{ mt: 1 }} />
              <Skeleton width="80%" height={24} sx={{ mt: 2 }} />
              <Skeleton width="80%" height={24} sx={{ mt: 1 }} />
            </Box>
          ) : unit ? (
            <>
              {/* Floor Plan Image */}
              {unit.floor_plan_image && (
                <Box
                  sx={{
                    width: "100%",
                    height: 200,
                    borderRadius: 2,
                    overflow: "hidden",
                    mb: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <img
                    src={unit.floor_plan_image}
                    alt={unit.model_name || "Floor Plan"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}

              {/* Unit Info */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="h5" fontWeight={700}>
                    Unit {unit.unit_number}
                  </Typography>
                  <Chip
                    label={availability?.text}
                    color={availability?.color}
                    size="small"
                  />
                </Box>
                {unit.model_name && (
                  <Typography variant="body2" color="text.secondary">
                    {unit.model_name}
                  </Typography>
                )}
              </Box>

              {/* Price Card */}
              <Card
                sx={{
                  mb: 2,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Monthly Rent
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="primary">
                        {formatCurrency(unit.current_price)}
                      </Typography>
                    </Box>
                    {unit.price_per_sqft && (
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="caption" color="text.secondary">
                          Per Sq Ft
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatPricePerSqft(unit.price_per_sqft)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Unit Specs */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                      textAlign: "center",
                    }}
                  >
                    <BedIcon sx={{ color: theme.palette.info.main, mb: 0.5 }} />
                    <Typography variant="body2" fontWeight={600}>
                      {unit.bedrooms === 0 ? "Studio" : `${unit.bedrooms} Bed`}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                      textAlign: "center",
                    }}
                  >
                    <BathIcon sx={{ color: theme.palette.info.main, mb: 0.5 }} />
                    <Typography variant="body2" fontWeight={600}>
                      {unit.bathrooms} Bath
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                      textAlign: "center",
                    }}
                  >
                    <SqftIcon sx={{ color: theme.palette.info.main, mb: 0.5 }} />
                    <Typography variant="body2" fontWeight={600}>
                      {formatSqft(unit.square_feet)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Details List */}
              <List dense disablePadding>
                {unit.available_date && (
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <EventIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Available Date"
                      secondary={formatDate(unit.available_date)}
                    />
                  </ListItem>
                )}
                {unit.deposit && (
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <MoneyIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Deposit"
                      secondary={formatCurrency(unit.deposit)}
                    />
                  </ListItem>
                )}
              </List>

              <Divider sx={{ my: 2 }} />

              {/* Property Info */}
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Property Information
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.grey[500], 0.08),
                  mb: 2,
                  cursor: onPropertyClick ? "pointer" : "default",
                  "&:hover": onPropertyClick
                    ? {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      }
                    : {},
                }}
                onClick={() =>
                  onPropertyClick && onPropertyClick(unit.property_id)
                }
              >
                <Avatar
                  src={unit.property_image || undefined}
                  variant="rounded"
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  <ApartmentIcon color="primary" />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {unit.property_name}
                  </Typography>
                  {unit.property_address && (
                    <Typography variant="caption" color="text.secondary">
                      {unit.property_address}
                    </Typography>
                  )}
                  {unit.property_neighborhood && (
                    <Chip
                      label={unit.property_neighborhood}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Contact Info */}
              <List dense disablePadding>
                {unit.property_phone && (
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PhoneIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Link
                          href={`tel:${unit.property_phone}`}
                          underline="hover"
                        >
                          {unit.property_phone}
                        </Link>
                      }
                    />
                  </ListItem>
                )}
                {unit.property_website && (
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <WebIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Link
                          href={unit.property_website}
                          target="_blank"
                          rel="noopener"
                          underline="hover"
                        >
                          Visit Website
                        </Link>
                      }
                    />
                  </ListItem>
                )}
                {unit.pets_allowed && (
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PetsIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Pet Friendly"
                      secondary={
                        unit.pet_deposit || unit.pet_rent
                          ? `Deposit: ${formatCurrency(unit.pet_deposit)} | Rent: ${formatCurrency(unit.pet_rent)}/mo`
                          : "Contact for details"
                      }
                    />
                  </ListItem>
                )}
              </List>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <ApartmentIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography>Select a unit to view details</Typography>
            </Box>
          )}
        </Box>

        {/* Footer Actions */}
        {unit && (
          <Box
            data-testid="unit-detail-actions"
            sx={{
              px: 2,
              pt: 2,
              pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              backgroundColor: "background.paper",
            }}
          >
            {externalApplication && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Button
                  variant="contained"
                  fullWidth
                  endIcon={<OpenIcon />}
                  href={externalApplication.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET }}
                >
                  Apply on property site
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5, textAlign: "center" }}
                >
                  Opens {externalApplication.hostname}
                </Typography>
              </Box>
            )}
            <Button
              variant="outlined"
              fullWidth
              sx={{
                flex: 1,
                minWidth: TOUCH_TARGET,
                minHeight: TOUCH_TARGET,
                alignSelf: externalApplication ? { xs: "stretch", sm: "flex-start" } : undefined,
              }}
              onClick={() =>
                onPropertyClick && onPropertyClick(unit.property_id)
              }
            >
              View Property
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default UnitDetailDrawer;
